import { Card, Icon, Loader, Typography } from '@bharatpe/bharat-ui';
import PageLayout from '@bharatpe/bharat-ui/web/PageLayout';
import React, { useEffect, useState } from 'react';
import { copyToClipboard, getURLParameter } from 'utils/nativeSupport';
import thumbsUp from 'assets/icons/Autopay/thumbsUp.svg';
import history from 'utils/History';
import copyIcon from 'assets/icons/Autopay/copyIcon.svg';

import { NavigateNext } from '@bharatpe/bharat-ui/web/Icons';
import { formatDateDynamic, isEmpty } from 'utils/Utils';
import { TriggerNativeApiWrapper } from 'utils/nativeUpiHandler';
import { API_URL } from 'Services/CONSTANT';
import Toasty from 'components/atoms/Toasty';
import AppHeader from 'components/atoms/AppHeader';
import UpiPoweredBy from 'components/atoms/UpiPoweredBy';
import secureGreen from 'assets/icons/Autopay/secureGreen.svg';
import { sendEvent } from 'utils/Sentry';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import './styles.scss';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';

const AutopayDetails: React.FC = () => {
	const [mandateData, setMandateData] = useState({});
	const [loading, setLoading] = useState(true);
	const {
		payee_name = '',
		payee_vpa = '',
		amount = '',
		frequency = '',
		mandate_id = '',
		umn = '',
		status = '',
		validity_start_date = '',
		validity_end_date = '',
		last_transaction_amount = '',
		payee_logo = '',
		payer_bank_logo = '',
		payer_bank_name = '',
		payer_bank_account = '',
	} = (mandateData as any) || {};
	const { languageData } = useLanguageConfig();

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AP_Details',
			Module: 'Page',
			Action: 'Viewed',
			mStatus: status,
		});
	}, []);

	useEffect(() => {
		if (!isEmpty(getURLParameter(window.location.href, 'mandateId'))) {
			(async () => {
				try {
					const data: any = await TriggerNativeApiWrapper(
						`${API_URL.mandate_details}/${getURLParameter(window.location.href, 'mandateId')}`,
						'GET',
						{},
						{},
					);
					setLoading(false);
					setMandateData(data);
				} catch (err: any) {
					sendEvent(err);
					Toasty.error(err?.error || 'something went wrong');
				}
			})();
		}
	}, []);

	const getTextVariant = (status: string) => {
		switch (status) {
			case 'ACTIVE':
			case 'HONOURED':
				return 'green-text-color';
				break;
			case 'REVOKED':
			case 'FAILED':
				return 'failed-logo ';
				break;
			case 'PENDING':
				return 'pending-logo';
				break;
			case 'INITIATED':
				return 'primary';
				break;
		}
	};

	const getStripVariant = (status: string) => {
		switch (status) {
			case 'ACTIVE':
				return 'success';
				break;
			case 'REVOKED':
			case 'FAILED':
				return 'danger';
				break;
			case 'PENDING':
				return 'warning';
				break;
			case 'INITIATED':
				return 'primary';
				break;
		}
	};

	const getBankCardStrip = (status: string) => {
		return {
			children: (
				<Typography className={`text-center ${getTextVariant(status)}`}>
					{`${languageData.AUTOPAY_IS} ${status.toUpperCase()}`}
				</Typography>
			),
			variant: getStripVariant(status),
			showIcon: false,
			className: 'flex-justify-center',
		};
	};

	const handleNavigateNext = (umn: string) => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Transaction history',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AP_Details',
			Module: 'Amount_detail',
			Element: 'CTA',
			Action: 'Tapped',
			mStatus: status,
			ctaType: 'transactionHistory',
		});
		history.push({
			pathname: '/account/transaction-list',
			search: `?umn=${umn}`,
		});
	};

	const onClickMandateId = () => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Mandate ID Copy',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AP_Details',
			Module: 'Amount_detail',
			Element: 'CTA',
			Action: 'Tapped',
			mStatus: status,
			ctaType: 'mandateIdCopy',
		});
		copyToClipboard({ code: mandate_id, lable: 'user-mandate-id', msgText: languageData.MANDATE_ID_COPIED });
	};

	const onClickUMRNNumber = () => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on UMRN Copy',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AP_Details',
			Module: 'Amount_detail',
			Element: 'CTA',
			Action: 'Tapped',
			mStatus: status,
			ctaType: 'umrnCopy',
		});
		copyToClipboard({ code: umn, lable: 'user-umn', msgText: languageData.UMN_COPIED });
	};

	const backEventAction = () => {
		logAnalyticsEvent({
			trigger_point: `Clicks on Back Button`,
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AP_Details',
			Element: 'back',
			Action: 'Tapped',
			mStatus: status,
			source: 'appHeader',
		});
	};

	return loading ? (
		<Loader loading />
	) : (
		<PageLayout className="white-background ">
			<>
				<AppHeader title={languageData.AUTOPAY_DETAILS} backEventAction={backEventAction} />
				<div className="mx5 mt6 mandate-details-content">
					<Card strip={getBankCardStrip(status)} className="p4 flex flex-row flex-center">
						<img src={payee_logo} height={32} width={50} className="mr2" />
						<div className="flex flex-column flex-start">
							<Typography variant="Regular300">{payee_name}</Typography>
							<Typography variant="Light100" className="grey">
								{payee_vpa}
							</Typography>
						</div>
					</Card>
					<Card className="p4 mb5 mt5 flex flex-column flex-center mb4">
						<div className="flex flex-row flex-justify flex-center width-100">
							<Typography variant="Bold300">{languageData.AMOUNT}</Typography>
							<div className="flex flex-column">
								<div>
									<Typography variant="Light100" className="mr2" component="span">
										{languageData.UP_TO}
									</Typography>
									<Typography variant="Bold700" component="span">
										₹{amount}
									</Typography>
								</div>
								<div className="auto-debit text-center">
									<Typography variant="Regular100" component="span">
										{languageData.AUTO_DEBITED}
									</Typography>
								</div>
							</div>
						</div>
						{(status === 'ACTIVE' || status === 'REVOKED') && (
							<>
								<div className="horizontal-dashed-divider my4"></div>
								<div className="flex flex-row flex-justify flex-center width-100">
									<div className="flex flex-row flex-center">
										<img className="mr2" src={thumbsUp} height={12} width={12} />
										<Typography variant="Regular200" className="grey">
											₹{last_transaction_amount} - {languageData.LAST_PAID}
										</Typography>
									</div>
									<div className="flex flex-row flex-center" onClick={() => handleNavigateNext(umn)}>
										<Typography variant="Regular200" className="underline primary">
											{languageData.TRANSACTION_HISTORY_2}
										</Typography>
										<Icon icon={NavigateNext} className="primary" />
									</div>
								</div>
							</>
						)}
					</Card>
					<Card className="p4 mb4 mt5 flex flex-column">
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light100" className="grey">
								{languageData.FREQUENCY}
							</Typography>
							<Typography variant="Regular200 mt2">{frequency}</Typography>
						</div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light100" className="grey">
								{languageData.AUTOPAY_VALIDITY}
							</Typography>
							<Typography variant="Regular200 mt2">
								<>
									{formatDateDynamic(validity_start_date, `DD MMM' YYYY`)}-
									{formatDateDynamic(validity_end_date, `DD MMM' YYYY`)}
								</>
							</Typography>
						</div>
						<div className="horizontal-dashed-divider bg-white mb3"></div>
						<div className="flex flex-row flex-justify flex-center mb3" onClick={onClickMandateId}>
							<div className="flex flex-column flex-start">
								<Typography variant="Light100" className="grey">
									{languageData.MANDATE_ID}
								</Typography>
								<Typography variant="Regular200 mt2">{mandate_id}</Typography>
							</div>
							<Icon icon={copyIcon} height={20} />
						</div>

						{!isEmpty(umn) && (
							<div className="flex flex-row flex-justify flex-center mb3" onClick={onClickUMRNNumber}>
								<div className="flex flex-column flex-start mb3 width-75">
									<Typography variant="Light100" className="grey">
										{languageData.UMRN}
									</Typography>
									<Typography variant="Regular200 mt2" className="text-ellipsis width-100">
										{umn}
									</Typography>
								</div>
								<Icon icon={copyIcon} height={20} />
							</div>
						)}
						<div className="horizontal-dashed-divider bg-white mb3"></div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light100" className="grey">
								{languageData.DEBIT_FROM}
							</Typography>
							<div className="flex flex-row flex-center">
								<img src={payer_bank_logo} loading="lazy" className="mt2 mr1" height={20} />
								<Typography variant="Regular200 mt2">
									<>
										{payer_bank_name} {payer_bank_account}
									</>
								</Typography>
							</div>
						</div>
					</Card>
					<div className="flex flex-row flex-justify-center flex-center">
						<img src={secureGreen} loading="lazy" className="mt2 mr1" height={20} />
						<Typography variant="Regular200" className="mt2 text-secure" component="span">
							{languageData.SEAMLESS_AND_SECURE}
						</Typography>
					</div>
				</div>
				<div className="upi-powered-sticky">
					<UpiPoweredBy />
				</div>
			</>
		</PageLayout>
	);
};

export default AutopayDetails;
